import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useFormik } from "formik";
import { ChangePasswordSchema } from "../../../schemas";
import Popup from "../../../base-components/Popup";
import { CircularProgress } from "@mui/material";
import AuthShell from "../../../ui/AuthShell";

const BASE_URL = process.env.REACT_APP_BASE_URL;

function ChangePassword() {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [user, setUser] = useState({});
  const [buttonLoading, setButtonLoading] = useState(false);

  useEffect(() => {
    const userInfo = localStorage.getItem("user-info");
    if (userInfo) {
      const session = JSON.parse(userInfo);
      setUser(session.user);
    }
  }, []);

  const handleChangePassword = async (values) => {
    if (values.newPassword !== values.confirmPassword) {
      setErrorMessage("New Password and Confirm Password do not match");
      setIsPopupOpen(true);
      setTimeout(() => setIsPopupOpen(false), 3000);
    }
    setButtonLoading(true);
    const data = {
      user_id: user._id,
      newPassword: values.newPassword,
      password: values.password,
    };

    try {
      const response = await fetch(BASE_URL + `users/change-password`, {
        method: "POST",
        body: JSON.stringify(data),
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        const responseBody = await response.json();
        localStorage.setItem("user-info", JSON.stringify(responseBody));
        sessionStorage.setItem("user-info", JSON.stringify(responseBody));
        navigate("/dashboard");
      } else {
        const responseBody = await response.json();
        setErrorMessage(responseBody.message);
        setIsPopupOpen(true);
        setTimeout(() => setIsPopupOpen(false), 3000);
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setButtonLoading(false);
    }
  };

  const { values, errors, touched, handleBlur, handleChange, handleSubmit } =
    useFormik({
      initialValues: {
        _id: user._id,
        password: "",
        newPassword: "",
        confirmPassword: "",
      },
      validationSchema: ChangePasswordSchema,
      onSubmit: handleChangePassword,
    });

  const fieldClass = (hasError) =>
    `h-11 w-full rounded-gx-control border bg-gx-surface px-3 text-gx-ink outline-none focus:ring-2 focus:ring-gx-teal ${
      hasError ? "border-gx-red" : "border-gx-border"
    }`;

  const PasswordField = ({
    id,
    label,
    name,
    shown,
    toggle,
    value,
    error,
  }) => (
    <div className="flex flex-col gap-2 mb-4">
      <label htmlFor={id} className="text-sm font-medium text-gx-ink">
        {label}
      </label>
      <div className="relative">
        <input
          id={id}
          type={shown ? "text" : "password"}
          name={name}
          className={fieldClass(error)}
          value={value}
          onChange={handleChange}
          onBlur={handleBlur}
        />
        <button
          type="button"
          aria-label={shown ? "Hide password" : "Show password"}
          className="absolute right-2 top-1/2 -translate-y-1/2 h-9 w-9 inline-flex items-center justify-center text-gx-ink-soft"
          onClick={toggle}
        >
          <FontAwesomeIcon icon={shown ? faEyeSlash : faEye} />
        </button>
      </div>
      {error ? <p className="text-sm text-gx-red">{error}</p> : null}
    </div>
  );

  return (
    <AuthShell title="Change password" subtitle="Choose a new password for your GymXS account.">
      <form onSubmit={handleSubmit} autoComplete="off">
        <PasswordField
          id="password"
          label="Current password"
          name="password"
          shown={showPassword}
          toggle={() => setShowPassword(!showPassword)}
          value={values.password}
          error={touched.password && errors.password}
        />
        <PasswordField
          id="newPassword"
          label="New password"
          name="newPassword"
          shown={showNewPassword}
          toggle={() => setShowNewPassword(!showNewPassword)}
          value={values.newPassword}
          error={touched.newPassword && errors.newPassword}
        />
        <PasswordField
          id="confirmPassword"
          label="Confirm new password"
          name="confirmPassword"
          shown={showConfirmPassword}
          toggle={() => setShowConfirmPassword(!showConfirmPassword)}
          value={values.confirmPassword}
          error={touched.confirmPassword && errors.confirmPassword}
        />
        <button
          type="submit"
          className="gx-cta w-full h-11"
          disabled={buttonLoading}
        >
          {buttonLoading ? (
            <span className="inline-flex items-center gap-2">
              Saving
              <CircularProgress size={18} color="inherit" />
            </span>
          ) : (
            "Save password"
          )}
        </button>
      </form>
      {isPopupOpen && (
        <Popup
          alertType="error"
          message={errorMessage}
          onClose={() => setIsPopupOpen(false)}
        />
      )}
    </AuthShell>
  );
}

export default ChangePassword;
